import React from 'react'
import { HiUserMinus, HiXMark, HiBolt } from 'react-icons/hi2'
import { BaseModal } from '../../../../components/Modal/BaseModal'
import { Button } from '../../../../components/Button'
import { FaUsers } from 'react-icons/fa'
import { UserAvatar } from '../../../../components/UserAvatar'
import type { User } from '../../../../models'

export interface AssignedUser {
  id: number
  usuario_fk: number
  active: boolean
  status: string
  usuario?: User
}

export interface RemoveAssignedModalProps {
  isOpen: boolean
  isMinimized: boolean
  isLoading: boolean
  assignedUsers: AssignedUser[]
  usersToRemove: number[]
  onMinimize: () => void
  onRestore: () => void
  onClose: () => void
  onConfirm: () => void
  onToggleUserRemoval: (userId: number) => void
}

/**
 * Modal para remover usuários atribuídos da demanda (apenas gestor)
 */
export const RemoveAssignedModal: React.FC<RemoveAssignedModalProps> = ({
  isOpen,
  isMinimized,
  isLoading,
  assignedUsers,
  usersToRemove,
  onMinimize,
  onRestore,
  onClose,
  onConfirm,
  onToggleUserRemoval,
}) => {
  const activeAssignedUsers = assignedUsers.filter(user => user.active && user.status !== 'RECUSADA')

  return (
    <BaseModal
      isOpen={isOpen}
      isMinimized={isMinimized}
      modalId='demand-remove-assigned'
      actionIcon={<HiUserMinus className='w-4 h-4' />}
      toolbarActionId='removeAssigned'
      onMinimize={onMinimize}
      onRestore={onRestore}
      onClose={onClose}
      title='Remover Atribuídos'
      description='Remova usuários que estão atribuídos a esta demanda. Esta ação só pode ser realizada por gestores.'
      useStandardLayout={true}
      size='md'
      resizable={true}
      stickyFooter={true}
      footerContent={
        <div className='flex justify-end space-x-3 p-4'>
          <Button variant='outline' onClick={onClose} disabled={isLoading} className='min-w-20'>
            Cancelar
          </Button>
          <Button
            variant='destructive'
            onClick={onConfirm}
            disabled={isLoading || usersToRemove.length === 0}
            isLoading={isLoading}
            className='min-w-24'
          >
            {isLoading ? 'Removendo...' : `Remover (${usersToRemove.length})`}
          </Button>
        </div>
      }
    >
      <div className='p-4 space-y-4'>
        {activeAssignedUsers.length === 0 ? (
          <div className='text-center py-8'>
            <div className='mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 mb-4'>
              <FaUsers className='w-6 h-6 text-gray-400' />
            </div>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-100 mb-2'>
              Nenhum usuário atribuído
            </h3>
            <p className='text-sm text-gray-500 dark:text-gray-400'>
              Não há usuários atribuídos ativos nesta demanda.
            </p>
          </div>
        ) : (
          <>
            {/* Usuários Atribuídos */}
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <FaUsers className='w-5 h-5 text-gray-600 dark:text-gray-400' />
                  <label className='block text-sm font-semibold text-gray-700 dark:text-gray-300'>
                    Usuários Atribuídos
                  </label>
                  <span className='text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded'>
                    Clique para marcar/desmarcar
                  </span>
                </div>
                {usersToRemove.length > 0 && (
                  <span className='text-sm text-red-600 dark:text-red-400 font-semibold bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded'>
                    {usersToRemove.length} marcado(s)
                  </span>
                )}
              </div>

              <div className='bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3'>
                <div className='space-y-2 max-h-64 overflow-y-auto'>
                  {activeAssignedUsers.map(assigned => {
                    const userId = assigned.usuario_fk
                    const isMarkedForRemoval = usersToRemove.includes(userId)
                    const isInProgress = assigned.status === 'EMPROGRESSO'

                    return (
                      <div
                        key={assigned.id}
                        className={`flex items-center justify-between p-3 border rounded-lg transition-all duration-200 ${
                          isMarkedForRemoval
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 cursor-pointer'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
                        }`}
                        onClick={() => onToggleUserRemoval(userId)}
                      >
                        <div className='flex items-center space-x-3'>
                          {/* Avatar do usuário com indicador de status */}
                          <div className='relative'>
                            {assigned.usuario ? (
                              <UserAvatar
                                user={assigned.usuario}
                                size='lg'
                                showPopup={false}
                                className={
                                  isMarkedForRemoval
                                    ? 'opacity-50 grayscale'
                                    : ''
                                }
                              />
                            ) : (
                              <div className='w-10 h-10 rounded-full flex items-center justify-center bg-gray-300 dark:bg-gray-600'>
                                <HiUserMinus className='w-5 h-5 text-gray-500 dark:text-gray-400' />
                              </div>
                            )}
                            
                            {/* Indicador de status sobreposto */}
                            <div
                              className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800 ${
                                isMarkedForRemoval
                                  ? 'bg-red-500'
                                  : isInProgress
                                    ? 'bg-green-500'
                                    : 'bg-blue-500'
                              }`}
                            >
                              {isMarkedForRemoval ? (
                                <HiXMark className='w-3 h-3 text-white' />
                              ) : isInProgress ? (
                                <HiBolt className='w-3 h-3 text-white' />
                              ) : (
                                <HiUserMinus className='w-3 h-3 text-white' />
                              )}
                            </div>
                          </div>

                          <div className='flex-1'>
                            <div className='flex items-center space-x-2'>
                              <span
                                className={`text-sm font-medium ${
                                  isMarkedForRemoval
                                    ? 'text-red-700 dark:text-red-300 line-through'
                                    : 'text-gray-900 dark:text-gray-100'
                                }`}
                              >
                                {assigned.usuario?.name || `Usuário Desconhecido (ID: ${userId})`}
                              </span>
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  assigned.status === 'EMPROGRESSO'
                                    ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300'
                                    : assigned.status === 'RECEBIDA'
                                      ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300'
                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {assigned.status === 'EMPROGRESSO'
                                  ? '🚀 Em Progresso'
                                  : assigned.status === 'RECEBIDA'
                                    ? '📬 Recebida'
                                    : assigned.status}
                              </span>
                            </div>
                            {assigned.usuario?.email && (
                              <p
                                className={`text-xs mt-0.5 ${
                                  isMarkedForRemoval
                                    ? 'text-red-500 dark:text-red-400'
                                    : 'text-gray-500 dark:text-gray-400'
                                }`}
                              >
                                {assigned.usuario.email}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className='flex items-center space-x-2'>
                          {isMarkedForRemoval ? (
                            <span className='text-xs text-red-600 dark:text-red-400 font-semibold bg-red-100 dark:bg-red-900 px-2 py-0.5 rounded'>
                              ✓ Será removido
                            </span>
                          ) : (
                            <span className='text-xs text-gray-500 dark:text-gray-400'>
                              Clique para remover
                            </span>
                          )}
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              isMarkedForRemoval
                                ? 'bg-red-600 border-red-600 shadow-sm'
                                : 'border-gray-300 dark:border-gray-500 hover:border-red-400 dark:hover:border-red-500'
                            }`}
                          >
                            {isMarkedForRemoval && <HiXMark className='w-3 h-3 text-white' />}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className='mt-3 pt-2 border-t border-gray-200 dark:border-gray-600'>
                  <p className='text-xs text-gray-600 dark:text-gray-400 flex items-center justify-between'>
                    <span>
                      {activeAssignedUsers.length} usuário(s) atribuído(s)
                      {usersToRemove.length > 0 && (
                        <span className='ml-2 text-red-600 dark:text-red-400 font-medium'>
                          • {usersToRemove.length} selecionado(s) para remoção
                        </span>
                      )}
                    </span>
                  </p>
                  <p className='text-xs text-amber-600 dark:text-amber-400 font-medium mt-1'>
                    ⚠️ Se todos os atribuídos forem removidos, a demanda voltará para &quot;Em Atribuição&quot;.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </BaseModal>
  )
}
